import { useDrawerStore } from '../../../store';
import { BaseModel, FormField, PageableResponse } from '../../../types';
import FormDrawer from '../../../components/ui/FormDrawer';
import {
  useCreateData,
  useFetchData,
  useUpdateData,
  useWarehouseStore,
} from '../../../hooks';
import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { Resource } from '../../../lib/enums';
import { WarehouseSchema } from '../../../lib/schemas';
import ErrorModal from '../../../components/ui/modal/ErrorModal';

const WarehouseForm = ({ api }: { api: string }) => {
  const drawerStore = useDrawerStore();
  const warehouseStore = useWarehouseStore();
  const [spinning, setSpinning] = useState<boolean>(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');

  const { data: cityData, isLoading: isCitiesLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('location/cities', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const { data: countryData, isLoading: isCountriesLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('location/countries', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const {
    mutate,
    isSuccess: isSuccessCreate,
    isError: isErrorCreate,
    error: errorCreate,
  } = useCreateData(api);
  const {
    mutate: mutateUpdate,
    isSuccess: isSuccessUpdate,
    isError: isErrorUpdate,
    error: errorUpdate,
  } = useUpdateData(api);

  const onFinish = (values: { city: string; country: string }) => {
    setSpinning(true);
    const city: BaseModel | undefined = cityData?.data?.find((obj) => {
      return obj.id == values.city;
    });

    const country: BaseModel | undefined = countryData?.data?.find((obj) => {
      return obj.id == values.country;
    });

    const jsonData = {
      ...values,
      city: city,
      country: country,
    };
    if (warehouseStore.data !== null) {
      const oldData = WarehouseSchema.parse(warehouseStore.data);

      const updatedData = {
        ...oldData,
        ...jsonData,
      };
      mutateUpdate({ id: oldData.id, form: updatedData });
    } else {
      mutate(jsonData);
    }
  };

  const formFields: FormField[] = [
    {
      name: 'code',
      label: 'Code',
      rules: [{ required: true, message: 'Please enter Code' }],
      disabled: warehouseStore.data != null,
      defaultValue: warehouseStore.data?.code,
    },
    {
      name: 'description',
      label: 'Description',
      rules: [{ required: true, message: 'Please enter description' }],
      defaultValue: warehouseStore.data?.description,
    },
    {
      name: 'address',
      label: 'Address',
      defaultValue: warehouseStore.data?.address,
    },
    {
      name: 'city',
      label: 'City',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      isLoading: isCitiesLoading,
      values: cityData?.data,
      defaultValue: warehouseStore.data?.city.id,
    },
    {
      name: 'country',
      label: 'Country',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      isLoading: isCountriesLoading,
      values: countryData?.data,
      defaultValue: warehouseStore.data?.country.id,
    },
  ];

  useEffect(() => {
    if (isSuccessCreate || isSuccessUpdate || isErrorUpdate || isErrorCreate)
      setSpinning(false);

    if (isErrorUpdate) {
      setInfoModalOpen(true);
      setErrorMessage(errorUpdate?.response?.data.message);
    }
    if (isErrorCreate) {
      setInfoModalOpen(true);
      setErrorMessage(errorCreate?.response?.data.message);
    }
  }, [
    isSuccessCreate,
    isSuccessUpdate,
    isErrorUpdate,
    isErrorCreate,
    errorCreate,
  ]);

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <FormDrawer
        isOpen={drawerStore.isOpen}
        onClose={drawerStore.onClose}
        data={warehouseStore.data}
        moduleName={'Warehouse'}
        setData={warehouseStore.setData}
        onFinish={onFinish}
        formFields={formFields}
      />
      <ErrorModal
        visible={isInfoModalOpen}
        message={errorMessage}
        onClose={() => setInfoModalOpen(false)}
      />
    </>
  );
};

export default WarehouseForm;
